import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useQueryClient } from 'react-query';
import { apiClient, getWithoutAuthRefresh, tryRefreshToken } from '../services/apiClient';
import {
  USER_KEY,
  LEGACY_TOKEN_KEY,
  LEGACY_REFRESH_TOKEN_KEY,
  REMEMBER_ME_KEY,
  TENANT_SCOPED_KEYS,
} from '../utils/storageKeys';
import { useIdleTimer } from '../hooks/useIdleTimer';
import SessionTimeoutDialog from '../components/auth/SessionTimeoutDialog';

// --------------------------------------------------------------------------
// Config — override via environment variables
// --------------------------------------------------------------------------

const getPositiveEnvMinutes = (
  rawValue: string | undefined,
  defaultMinutes: number,
): number => {
  const parsedMinutes = parseInt(rawValue ?? '', 10);
  return Number.isFinite(parsedMinutes) && parsedMinutes > 0
    ? parsedMinutes
    : defaultMinutes;
};

/** Total idle time (ms) before the user is logged out. Default: 30 minutes. */
const IDLE_TIMEOUT_MS =
  getPositiveEnvMinutes(process.env.REACT_APP_IDLE_TIMEOUT_MINUTES, 30) * 60 * 1000;

/** Requested warning duration from env (ms). Default: 2 minutes. */
const CONFIGURED_WARNING_DURATION_MS =
  getPositiveEnvMinutes(process.env.REACT_APP_IDLE_WARNING_MINUTES, 2) * 60 * 1000;

/** Effective warning duration, clamped to match hook behavior. */
const WARNING_DURATION_MS = Math.min(CONFIGURED_WARNING_DURATION_MS, Math.floor(IDLE_TIMEOUT_MS / 2));

/** Warning dialog countdown in seconds (derived from WARNING_DURATION_MS). */
const WARNING_COUNTDOWN_SECONDS = WARNING_DURATION_MS / 1000;

// --------------------------------------------------------------------------
// Types
// --------------------------------------------------------------------------

export interface AuthUser {
  user_id: string;
  email: string;
  role: string;
  first_name: string;
  last_name?: string;
  tenant_id?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (
    email: string,
    password: string,
    mfaCode?: string,
    mfaBackupCode?: string,
  ) => Promise<{ success: boolean; error?: string; mfaRequired?: boolean; mfaEnrollmentRequired?: boolean }>;
  logout: () => Promise<void>;
  updateProfile: (data: { first_name: string; last_name?: string; email: string }) => Promise<{ success: boolean; error?: string }>;
  changePassword: (data: { current_password: string; new_password: string }) => Promise<{ success: boolean; error?: string }>;
  /** Returns true if the current user's role exactly matches the given role. */
  hasRole: (role: string) => boolean;
  /** Returns true if the current user has ANY of the given roles. */
  hasAnyRole: (roles: string[]) => boolean;
}

// --------------------------------------------------------------------------
// Context
// --------------------------------------------------------------------------

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// --------------------------------------------------------------------------
// Provider
// --------------------------------------------------------------------------

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * One-time scrub of any JWTs left over in localStorage from the pre-cookie
 * rollout.  Safe to run on every load.
 */
const scrubLegacyTokens = () => {
  localStorage.removeItem(LEGACY_TOKEN_KEY);
  localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
  sessionStorage.removeItem(LEGACY_TOKEN_KEY);
  sessionStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
};

const readCachedUserJson = (): string | null =>
  localStorage.getItem(USER_KEY) ?? sessionStorage.getItem(USER_KEY);

const removeCachedUserEverywhere = () => {
  localStorage.removeItem(USER_KEY);
  sessionStorage.removeItem(USER_KEY);
};

const persistCachedUser = (userJson: string) => {
  localStorage.setItem(USER_KEY, userJson);
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);

  const clearLocalClientSession = useCallback(() => {
    removeCachedUserEverywhere();
    scrubLegacyTokens();
    localStorage.removeItem(REMEMBER_ME_KEY);
  }, []);

  // On mount: hydrate from cached profile (fast render), then re-verify
  // against the backend.  If /me succeeds the cookie is still valid and the
  // user stays signed in; if it fails we clear everything.
  useEffect(() => {
    scrubLegacyTokens();
    const savedUser = readCachedUserJson();
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser) as AuthUser);
      } catch {
        removeCachedUserEverywhere();
      }
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await getWithoutAuthRefresh<AuthUser>('/api/v1/auth/me');
        if (cancelled) return;
        const fresh = res.data;
        setUser(fresh);
        persistCachedUser(JSON.stringify(fresh));
      } catch {
        if (cancelled) return;
        clearLocalClientSession();
        setUser(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [clearLocalClientSession]);

  const logout = useCallback(async () => {
    try {
      await apiClient.post('/api/v1/auth/logout');
    } catch {
      /* noop */
    }

    clearLocalClientSession();

    for (const key of TENANT_SCOPED_KEYS) {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    }

    queryClient.clear();
    setUser(null);
    setShowTimeoutWarning(false);
  }, [queryClient, clearLocalClientSession]);

  const { resetTimer } = useIdleTimer({
    idleTimeout: IDLE_TIMEOUT_MS,
    warningDuration: WARNING_DURATION_MS,
    enabled: !!user,
    onWarning: useCallback(() => setShowTimeoutWarning(true), []),
    onIdle: useCallback(() => {
      void logout();
    }, [logout]),
    onActivity: useCallback(() => setShowTimeoutWarning(false), []),
  });

  const handleStayLoggedIn = useCallback(async () => {
    const ok = await tryRefreshToken();
    if (!ok) {
      await logout();
      return;
    }
    setShowTimeoutWarning(false);
    resetTimer();
  }, [logout, resetTimer]);

  const login = useCallback(
    async (
      email: string,
      password: string,
      mfaCode?: string,
      mfaBackupCode?: string,
    ): Promise<{ success: boolean; error?: string; mfaRequired?: boolean; mfaEnrollmentRequired?: boolean }> => {
      try {
        for (const key of TENANT_SCOPED_KEYS) {
          localStorage.removeItem(key);
          sessionStorage.removeItem(key);
        }
        clearLocalClientSession();
        queryClient.clear();

        await apiClient.post('/api/v1/auth/login', {
          email,
          password,
          mfa_code: mfaCode || undefined,
          mfa_backup_code: mfaBackupCode || undefined,
        });

        const meResponse = await apiClient.get('/api/v1/auth/me');
        const userData: AuthUser = meResponse.data;
        persistCachedUser(JSON.stringify(userData));
        setUser(userData);

        return { success: true };
      } catch (err: any) {
        const data = err?.response?.data;
        const detail = data?.detail;

        const legacyStringMfa = (() => {
          if (typeof detail !== 'string') return null;
          const s = detail.trim();
          if (!s.includes('mfa_required')) return null;
          const jsonLike = s
            .replace(/'/g, '"')
            .replace(/\bTrue\b/g, 'true')
            .replace(/\bFalse\b/g, 'false')
            .replace(/\bNone\b/g, 'null');
          try {
            const o = JSON.parse(jsonLike) as { mfa_required?: boolean; message?: string };
            if (o && o.mfa_required) return o;
          } catch {
            /* ignore */
          }
          return null;
        })();

        const mfaObject =
          legacyStringMfa ||
          (detail && typeof detail === 'object' && !Array.isArray(detail) && (detail as { mfa_required?: boolean }).mfa_required
            ? detail
            : null);
        if (mfaObject) {
          const msg =
            typeof (mfaObject as { message?: unknown }).message === 'string'
              ? (mfaObject as { message: string }).message
              : 'MFA code required';
          return {
            success: false,
            mfaRequired: true,
            mfaEnrollmentRequired: Boolean(
              (mfaObject as { mfa_enrollment_required?: boolean }).mfa_enrollment_required,
            ),
            error: msg,
          };
        }
        const message =
          data?.error ||
          (typeof detail === 'string' ? detail : undefined) ||
          (detail && typeof detail === 'object' && !Array.isArray(detail) ? (detail as { message?: string }).message : undefined) ||
          err?.message ||
          'Login failed. Please try again.';
        return { success: false, error: message };
      }
    },
    [queryClient, clearLocalClientSession],
  );

  const updateProfile = useCallback(
    async (data: { first_name: string; last_name?: string; email: string }): Promise<{ success: boolean; error?: string }> => {
      try {
        await apiClient.put('/api/v1/auth/me', data);

        const meResponse = await apiClient.get('/api/v1/auth/me');
        const userData: AuthUser = meResponse.data;
        persistCachedUser(JSON.stringify(userData));
        setUser(userData);

        return { success: true };
      } catch (err: any) {
        const message = err?.response?.data?.detail || err?.message || 'Failed to update profile.';
        return { success: false, error: message };
      }
    },
    [],
  );

  const changePassword = useCallback(
    async (data: { current_password: string; new_password: string }): Promise<{ success: boolean; error?: string }> => {
      try {
        await apiClient.put('/api/v1/auth/me/password', data);
        return { success: true };
      } catch (err: any) {
        const message = err?.response?.data?.detail || err?.message || 'Failed to change password.';
        return { success: false, error: message };
      }
    },
    [],
  );

  const hasRole = useCallback(
    (role: string): boolean => user?.role === role,
    [user],
  );

  const hasAnyRole = useCallback(
    (roles: string[]): boolean => roles.some((r) => r === user?.role),
    [user],
  );

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    updateProfile,
    changePassword,
    hasRole,
    hasAnyRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <SessionTimeoutDialog
        open={showTimeoutWarning}
        countdownSeconds={WARNING_COUNTDOWN_SECONDS}
        onStayLoggedIn={handleStayLoggedIn}
        onLogOut={() => {
          void logout();
        }}
      />
    </AuthContext.Provider>
  );
};

export default AuthContext;
